import { useEffect, useRef, useState, ReactNode, useLayoutEffect } from "react";

declare global {
    interface Window {
        _debugMode: boolean;
        _debugStatesByComponent: Record<string, Record<string, any>>;
    }
}

function isDebugEnabled(): boolean {
    return typeof window !== "undefined" && window._debugMode === true;
}

export function useDebugState<T>(
    initialValue: T,
    name: string,
    debugKey: string,
    enabled: boolean = true
): [T, (value: T)=> void] {
    const [value, setValue] = useState<T>(initialValue);

    useLayoutEffect(() => {
        if (!enabled || !isDebugEnabled()) return;

        window._debugStatesByComponent = window._debugStatesByComponent || {};
        window._debugStatesByComponent[debugKey] = window._debugStatesByComponent[debugKey] || {};
        window._debugStatesByComponent[debugKey][name] = value;

        return () => {
            // Optional cleanup (in case state is removed mid-lifecycle)
            delete window._debugStatesByComponent?.[debugKey]?.[name];
        };
    }, [value, name, debugKey, enabled]);

    return [value, setValue];
}

interface StateDebuggerProps {
    debugKey: string;
    componentRenderCount?: number;
    children: ReactNode;
}

export const StateDebugger = ({
    debugKey,
    componentRenderCount = 0,
    children
}: StateDebuggerProps) => {
    const renderCount = useRef(0);
    const prevStatesRef = useRef<Record<string, any>>({});

    useEffect(() => {
        renderCount.current += 1;

        if (!isDebugEnabled()) return;

        const allStates = window._debugStatesByComponent?.[debugKey] || {};

        const changes = Object
            .entries(allStates)
            .filter(([key, value]) => prevStatesRef.current[key] !== value)
            .map(([key, value]) => ({
                State: key,
                Prev: JSON.stringify(prevStatesRef.current[key]),
                Current: JSON.stringify(value),
                Time: new Date().toISOString()
            }));

        if (changes.length > 0) {
            console.group(`🔍 StateDebugger [${debugKey}] render(${componentRenderCount})`);
            console.table(changes, ["State", "Prev", "Current", "Time"]);
            console.groupEnd();
        }

        prevStatesRef.current = { ...allStates };

        return () => {
            delete window._debugStatesByComponent?.[debugKey];
        };
    });

    return <>{children}</>;
};